import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Text, Grid, HStack, Box } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { RETRIEVE_CHANGES_LOG, GET_DASHBOARD_BOOKING_CONFIRMATION_AUTHENTICATED } from '@whitbread-eos/api';
import { ModalVariants, LoadingSpinner, Dropdown, Button } from '@whitbread-eos/atoms';
import { useQueryRequest, useCustomLocale, getAuthCookie, formatDataTestId, graphQLRequest } from '@whitbread-eos/utils';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import ChangeLogTable from './ChangeLogTable.component';
import { getTableColumns } from './tableConfig';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getChangesLogResults(_x, _x2, _x3, _x4, _x5) {
  return _getChangesLogResults.apply(this, arguments);
}
function _getChangesLogResults() {
  _getChangesLogResults = _asyncToGenerator(function* (queryClient, hotelId, selectedReservationId, limit, offset) {
    var queryResult = yield queryClient.fetchQuery(_objectSpread({
      queryKey: ['retrieveChangesLog', hotelId, selectedReservationId, limit, offset],
      queryFn: () => graphQLRequest(RETRIEVE_CHANGES_LOG, {
        hotelId,
        reservationId: selectedReservationId,
        limit,
        offset
      })
    }, {
      gcTime: 0
    }));
    var results = queryResult.retrieveChangesLog.activityLog;
    return results;
  });
  return _getChangesLogResults.apply(this, arguments);
}
var defaultChangeLogResults = {
  activityLog: [],
  hasMore: false,
  limit: 20,
  offset: 0,
  totalPages: 0,
  totalResults: 0,
  isLoadingChangeLog: false,
  isErrorChangeLog: false
};
function ChangeLog(_ref) {
  var showChangeLogModal = _ref.showChangeLogModal,
    setShowChangeLogModal = _ref.setShowChangeLogModal,
    bookingReference = _ref.bookingReference;
  var baseDataTestId = 'ChangeLog';
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    hotelId = _useState2[0],
    setHotelId = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    reservationIdList = _useState4[0],
    setReservationIdList = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedReservationId = _useState6[0],
    setSelectedReservationId = _useState6[1];
  var _useState7 = useState(defaultChangeLogResults),
    _useState8 = _slicedToArray(_useState7, 2),
    changeLogResults = _useState8[0],
    setChangeLogResults = _useState8[1];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useCustomLocale = useCustomLocale(),
    language = _useCustomLocale.language,
    country = _useCustomLocale.country;
  var idTokenCookie = getAuthCookie();
  var queryClient = useQueryClient();
  var isValidReservation = !!hotelId && (reservationIdList === null || reservationIdList === void 0 ? void 0 : reservationIdList.length) && !!selectedReservationId;
  var isMultiRoomReservatoin = (reservationIdList === null || reservationIdList === void 0 ? void 0 : reservationIdList.length) > 1;
  var _useQueryRequest = useQueryRequest(['getBookingConfirmationAuthenticated', bookingReference, language, country], GET_DASHBOARD_BOOKING_CONFIRMATION_AUTHENTICATED, {
      language,
      country,
      bookingReference
    }, {
      enabled: !!idTokenCookie
    }, idTokenCookie, true),
    bookingData = _useQueryRequest.data,
    isLoadingBookingData = _useQueryRequest.isLoading,
    isErrorBookingData = _useQueryRequest.isError;
  var enableChangesLogRequest = Boolean(!isLoadingBookingData && !isErrorBookingData && isValidReservation);
  var fetchChangeLogs = (offset, limit) => {
    setChangeLogResults(() => _objectSpread(_objectSpread({}, changeLogResults), {}, {
      isLoadingChangeLog: true
    }));
    getChangesLogResults(queryClient, hotelId, selectedReservationId, limit, offset).then(results => {
      if (results) {
        var latestResults = _objectSpread(_objectSpread({}, results), {}, {
          activityLog: [...changeLogResults.activityLog, ...results.activityLog],
          isLoadingChangeLog: false,
          isErrorChangeLog: false
        });
        setChangeLogResults(() => _objectSpread({}, latestResults));
      }
    }).catch(() => {
      setChangeLogResults(() => _objectSpread(_objectSpread({}, changeLogResults), {}, {
        isErrorChangeLog: true,
        isLoadingChangeLog: false
      }));
    });
  };
  useEffect(() => {
    if (enableChangesLogRequest) {
      fetchChangeLogs(changeLogResults.offset, changeLogResults.limit);
    }
  }, [hotelId, selectedReservationId, enableChangesLogRequest]);
  useEffect(() => {
    var _bookingData$bookingC, _bookingData$bookingC2;
    var hotelId = bookingData === null || bookingData === void 0 || (_bookingData$bookingC = bookingData.bookingConfirmationAuthenticated) === null || _bookingData$bookingC === void 0 ? void 0 : _bookingData$bookingC.hotelId;
    var reservationIdList = bookingData === null || bookingData === void 0 || (_bookingData$bookingC2 = bookingData.bookingConfirmationAuthenticated) === null || _bookingData$bookingC2 === void 0 || (_bookingData$bookingC2 = _bookingData$bookingC2.reservationByIdList) === null || _bookingData$bookingC2 === void 0 ? void 0 : _bookingData$bookingC2.map(x => x.reservationId);
    var firstReservationId = reservationIdList !== null && reservationIdList !== void 0 && reservationIdList.length ? reservationIdList[0] : '';
    setHotelId(hotelId !== null && hotelId !== void 0 ? hotelId : '');
    setReservationIdList(reservationIdList !== null && reservationIdList !== void 0 ? reservationIdList : []);
    setSelectedReservationId(firstReservationId);
  }, [bookingData]);
  var handleDropdown = option => {
    var _ref2;
    setChangeLogResults(defaultChangeLogResults);
    setSelectedReservationId((_ref2 = option === null || option === void 0 ? void 0 : option.id) !== null && _ref2 !== void 0 ? _ref2 : '');
  };
  var modalHeader = _jsxs(HStack, {
    gap: 4,
    children: [_jsx(Text, _objectSpread(_objectSpread({}, headerTitleStyle), {}, {
      "data-testid": formatDataTestId(baseDataTestId, 'ModalTitle'),
      children: t('ccui.changeLogModal.title')
    })), isMultiRoomReservatoin && _jsx(Dropdown, {
      dataTestId: formatDataTestId(baseDataTestId, 'MultiRoomDropdown'),
      dropdownStyles: dropdownStyles,
      matchWidth: true,
      selectedId: selectedReservationId,
      onChange: handleDropdown,
      options: reservationIdList.map((reservationId, index) => ({
        label: "".concat(t('ccui.changeLogModal.multiRoomDropdown.room').replace('[roomNumber]', String(index + 1))),
        id: reservationId
      }))
    })]
  });
  var variantProps = {
    title: t('ccui.changeLogModal.title'),
    modalHeight: modalHeight,
    header: modalHeader,
    showDelimiter: false
  };
  var renderModalBody = () => {
    if (isLoadingBookingData || changeLogResults.isLoadingChangeLog) {
      return _jsx(Grid, _objectSpread(_objectSpread({}, gridStyles), {}, {
        children: _jsx(LoadingSpinner, {
          loadingText: t('ccui.changeLogModal.loading')
        })
      }));
    }
    if (isErrorBookingData || changeLogResults.isErrorChangeLog) {
      return _jsxs(Grid, _objectSpread(_objectSpread({}, gridStyles), {}, {
        children: [_jsx(Text, _objectSpread(_objectSpread({}, messageTitleStyles), {}, {
          children: t('ccui.changeLogModal.errorHeading')
        })), _jsx(Text, _objectSpread(_objectSpread({}, errorMessageDescriptionStyles), {}, {
          children: t('ccui.changeLogModal.errorDescription')
        }))]
      }));
    }
    var rows = changeLogResults.activityLog;
    if (!(rows !== null && rows !== void 0 && rows.length)) {
      return _jsx(Grid, _objectSpread(_objectSpread({}, gridStyles), {}, {
        children: _jsx(Text, _objectSpread(_objectSpread({}, messageTitleStyles), {}, {
          children: t('ccui.changeLogModal.noResults')
        }))
      }));
    }
    var columns = getTableColumns(t);
    return _jsx(ChangeLogTable, {
      columns: columns,
      rows: rows
    });
  };
  var handleLoadMore = () => {
    fetchChangeLogs(changeLogResults.offset, changeLogResults.limit);
  };
  return _jsxs(ModalVariants, {
    isOpen: showChangeLogModal,
    onClose: () => {
      setShowChangeLogModal(false);
    },
    variant: "data",
    variantProps: variantProps,
    updatedWidth: modalWidth,
    dataTestId: baseDataTestId,
    children: [renderModalBody(), changeLogResults.hasMore && !changeLogResults.isLoadingChangeLog && _jsx(Box, _objectSpread(_objectSpread({}, loadMoreContainerStyle), {}, {
      children: _jsx(Button, {
        color: "btnSecondaryEnabled",
        variant: "tertiary",
        size: "md",
        onClick: handleLoadMore,
        "data-testid": formatDataTestId(baseDataTestId, 'LoadMoreButton'),
        children: t('ccui.changeLogModal.loadMore')
      })
    }))]
  });
}
var modalWidth = {
  lg: '1224px',
  xl: '1308px'
};
var modalHeight = {
  lg: '984px'
};
var headerTitleStyle = {
  color: 'darkGrey1',
  justify: 'center',
  justifyContent: 'flex-start',
  fontSize: 'lg',
  lineHeight: '3',
  fontFamily: 'header',
  padding: 'var(--chakra-sizes-3-5) 0',
  margin: 0
};
var dropdownStyles = {
  wrapperStyles: {
    width: '288px'
  },
  menuButtonStyles: {
    borderColor: 'var(--chakra-colors-lightGrey1)'
  },
  menuListStyles: {
    maxHeight: '200px'
  }
};
var loadMoreContainerStyle = {
  textAlign: 'center',
  my: 'lg'
};
var gridStyles = {
  height: '100%',
  justifyContent: 'center',
  alignContent: 'center',
  justifyItems: 'center'
};
var messageTitleStyles = {
  color: 'darkGrey1',
  fontSize: '3xl',
  lineHeight: '5',
  fontWeight: 'semibold',
  mb: 'var(--chakra-space-4)'
};
var errorMessageDescriptionStyles = {
  color: 'darkGrey1',
  fontSize: 'lg',
  lineHeight: '3',
  fontWeight: 'normal'
};
export default ChangeLog;